// Zustand store for state management - shows modern React patterns

import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { QueryFilters, PaginationParams, OpportunityStage, LeadSource } from '../types/salesforce';

interface FilterState {
  // Current filters
  filters: QueryFilters;
  pagination: PaginationParams;

  // UI state
  activeView: 'dashboard' | 'pipeline' | 'leads' | 'analytics';
  sidebarCollapsed: boolean;

  // Quick filters
  quickFilters: {
    thisWeek: boolean;
    thisMonth: boolean;
    thisQuarter: boolean;
    myOpportunities: boolean;
    highValue: boolean;
  };

  // Actions
  setFilters: (filters: Partial<QueryFilters>) => void;
  resetFilters: () => void;
  setPagination: (pagination: Partial<PaginationParams>) => void;
  setActiveView: (view: FilterState['activeView']) => void;
  toggleSidebar: () => void;
  setQuickFilter: (key: keyof FilterState['quickFilters'], value: boolean) => void;
  applyQuickFilter: (filterKey: keyof FilterState['quickFilters']) => void;

  // Computed getters
  hasActiveFilters: () => boolean;
  getFilterCount: () => number;
  getCurrentDateRange: () => { start: string; end: string } | undefined;
}

// Default values
const defaultFilters: QueryFilters = {};

const defaultPagination: PaginationParams = {
  offset: 0,
  limit: 50,
  sortBy: 'CreatedDate',
  sortOrder: 'DESC',
};

const defaultQuickFilters = {
  thisWeek: false,
  thisMonth: false,
  thisQuarter: false,
  myOpportunities: false,
  highValue: false,
};

// Create store with persistence and subscriptions
export const useFilters = create<FilterState>()(
  subscribeWithSelector(
    persist(
      (set, get) => ({
        // Initial state
        filters: defaultFilters,
        pagination: defaultPagination,
        activeView: 'dashboard',
        sidebarCollapsed: false,
        quickFilters: defaultQuickFilters,

        // Actions
        setFilters: (newFilters) => {
          set((state) => ({
            filters: { ...state.filters, ...newFilters },
            // Reset pagination when filters change
            pagination: { ...state.pagination, offset: 0 },
          }));
        },

        resetFilters: () => {
          set({
            filters: defaultFilters,
            pagination: defaultPagination,
            quickFilters: defaultQuickFilters,
          });
        },

        setPagination: (newPagination) => {
          set((state) => ({
            pagination: { ...state.pagination, ...newPagination },
          }));
        },

        setActiveView: (view) => {
          set({ activeView: view });
        },

        toggleSidebar: () => {
          set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed }));
        },

        setQuickFilter: (key, value) => {
          set((state) => ({
            quickFilters: { ...state.quickFilters, [key]: value },
          }));
        },

        applyQuickFilter: (filterKey) => {
          const state = get();
          const newQuickFilters = { ...defaultQuickFilters, [filterKey]: true };

          let newFilters: Partial<QueryFilters> = {};

          // Apply date range based on quick filter
          const now = new Date();
          switch (filterKey) {
            case 'thisWeek':
              const startOfWeek = new Date(now.setDate(now.getDate() - now.getDay()));
              newFilters.dateRange = {
                start: startOfWeek.toISOString().split('T')[0],
                end: new Date().toISOString().split('T')[0],
              };
              break;

            case 'thisMonth':
              const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
              newFilters.dateRange = {
                start: startOfMonth.toISOString().split('T')[0],
                end: new Date().toISOString().split('T')[0],
              };
              break;

            case 'thisQuarter':
              const quarter = Math.floor(now.getMonth() / 3);
              const startOfQuarter = new Date(now.getFullYear(), quarter * 3, 1);
              newFilters.dateRange = {
                start: startOfQuarter.toISOString().split('T')[0],
                end: new Date().toISOString().split('T')[0],
              };
              break;

            case 'myOpportunities':
              // In production, this would use current user ID
              newFilters.ownerId = 'current-user-id';
              break;

            case 'highValue':
              newFilters.minAmount = 10000;
              break;
          }

          set({
            quickFilters: newQuickFilters,
            filters: { ...state.filters, ...newFilters },
            pagination: { ...state.pagination, offset: 0 },
          });
        },

        // Computed getters
        hasActiveFilters: () => {
          const { filters } = get();
          return Object.keys(filters).length > 0 &&
                 Object.values(filters).some(value =>
                   value !== undefined && value !== null &&
                   (Array.isArray(value) ? value.length > 0 : true)
                 );
        },

        getFilterCount: () => {
          const { filters } = get();
          return Object.values(filters).filter(value =>
            value !== undefined && value !== null &&
            (Array.isArray(value) ? value.length > 0 : true)
          ).length;
        },

        getCurrentDateRange: () => {
          const { filters } = get();
          return filters.dateRange;
        },
      }),
      {
        name: 'sales-dashboard-filters',
        storage: createJSONStorage(() => localStorage),
        // Only persist certain fields
        partialize: (state) => ({
          filters: state.filters,
          activeView: state.activeView,
          sidebarCollapsed: state.sidebarCollapsed,
        }),
      }
    )
  )
);

// Derived selectors for complex state
export const useFilterSelectors = () => {
  const state = useFilters();

  return {
    // Active stage filters
    activeStages: state.filters.stage || [],

    // Active lead sources
    activeLeadSources: state.filters.leadSource || [],

    // Date range summary
    dateRangeSummary: (() => {
      const range = state.getCurrentDateRange();
      if (!range) return 'All time';

      const start = new Date(range.start);
      const end = new Date(range.end);
      const diffDays = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));

      if (diffDays <= 7) return 'This week';
      if (diffDays <= 31) return 'This month';
      if (diffDays <= 93) return 'This quarter';
      return `${diffDays} days`;
    })(),

    // Amount range summary
    amountRangeSummary: (() => {
      const { minAmount, maxAmount } = state.filters;
      if (!minAmount && !maxAmount) return undefined;

      const format = (amount: number) =>
        new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(amount);

      if (minAmount && maxAmount) return `${format(minAmount)} - ${format(maxAmount)}`;
      if (minAmount) return `${format(minAmount)}+`;
      if (maxAmount) return `Up to ${format(maxAmount)}`;
      return undefined;
    })(),

    // Quick filter labels
    activeQuickFilterLabels: Object.entries(state.quickFilters)
      .filter(([_, active]) => active)
      .map(([key]) => {
        const labels = {
          thisWeek: 'This Week',
          thisMonth: 'This Month',
          thisQuarter: 'This Quarter',
          myOpportunities: 'My Opportunities',
          highValue: 'High Value'
        };
        return labels[key as keyof typeof labels];
      }),
  };
};

// Advanced filter operations
export const useAdvancedFilters = () => {
  const { setFilters, filters } = useFilters();

  return {
    // Add stage filter
    addStageFilter: (stage: OpportunityStage) => {
      const currentStages = filters.stage || [];
      if (!currentStages.includes(stage)) {
        setFilters({ stage: [...currentStages, stage] });
      }
    },

    // Remove stage filter
    removeStageFilter: (stage: OpportunityStage) => {
      const currentStages = filters.stage || [];
      setFilters({ stage: currentStages.filter(s => s !== stage) });
    },

    // Toggle stage filter
    toggleStageFilter: (stage: OpportunityStage) => {
      const currentStages = filters.stage || [];
      const isActive = currentStages.includes(stage);

      if (isActive) {
        setFilters({ stage: currentStages.filter(s => s !== stage) });
      } else {
        setFilters({ stage: [...currentStages, stage] });
      }
    },

    // Similar methods for lead sources
    addLeadSourceFilter: (source: LeadSource) => {
      const currentSources = filters.leadSource || [];
      if (!currentSources.includes(source)) {
        setFilters({ leadSource: [...currentSources, source] });
      }
    },

    removeLeadSourceFilter: (source: LeadSource) => {
      const currentSources = filters.leadSource || [];
      setFilters({ leadSource: currentSources.filter(s => s !== source) });
    },

    // Set date range with presets
    setDateRange: (preset: 'today' | 'week' | 'month' | 'quarter' | 'year' | 'custom', customRange?: { start: string; end: string }) => {
      const now = new Date();
      let dateRange: { start: string; end: string };

      switch (preset) {
        case 'today':
          dateRange = {
            start: now.toISOString().split('T')[0],
            end: now.toISOString().split('T')[0],
          };
          break;

        case 'week':
          const startOfWeek = new Date(now.setDate(now.getDate() - now.getDay()));
          dateRange = {
            start: startOfWeek.toISOString().split('T')[0],
            end: new Date().toISOString().split('T')[0],
          };
          break;

        case 'month':
          const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
          dateRange = {
            start: startOfMonth.toISOString().split('T')[0],
            end: new Date().toISOString().split('T')[0],
          };
          break;

        case 'quarter':
          const quarter = Math.floor(now.getMonth() / 3);
          const startOfQuarter = new Date(now.getFullYear(), quarter * 3, 1);
          dateRange = {
            start: startOfQuarter.toISOString().split('T')[0],
            end: new Date().toISOString().split('T')[0],
          };
          break;

        case 'year':
          const startOfYear = new Date(now.getFullYear(), 0, 1);
          dateRange = {
            start: startOfYear.toISOString().split('T')[0],
            end: new Date().toISOString().split('T')[0],
          };
          break;

        case 'custom':
          if (!customRange) throw new Error('Custom range required for custom preset');
          dateRange = customRange;
          break;
      }

      setFilters({ dateRange });
    },

    // Set amount range
    setAmountRange: (min?: number, max?: number) => {
      setFilters({
        minAmount: min,
        maxAmount: max,
      });
    },
  };
};

// Subscribe to filter changes for analytics
useFilters.subscribe(
  (state) => state.filters,
  (filters) => {
    // In production, track filter usage for analytics
    console.log('Filters changed:', filters);
  }
);

export default useFilters;